import { baseFastApiFirmRoute } from "@/lib/data/requests/firm/firm";

import {
  authenticatedFastApiAxiosInstanceAllowEmptyString,
  publicFastApiAxiosInstance,
} from "@/lib/data/http";
import { baseContactRoute } from "@/lib/data/requests/contacts/contact";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import { getFirmSignatureRequestPublicFileUrlV2 } from "@/lib/data/requests/firm/firm-signature-request";
import { getMatterSignatureRequestPublicFileUrlV2 } from "@/lib/data/requests/matters/matter-signature-request";
import type { APIResponse } from "@/lib/types/APIResponse";

// Common types
type S3RequestData =
  | {
      clientMethod: "put_object";
      fileName?: string;
      key?: string;
      firmId?: string;
      signatureRequestPasscode?: string;
      shareFolderPasscode?: string;
    }
  | {
      clientMethod: "get_object" | "delete_object";
      key: string;
      signatureRequestPasscode?: string;
      shareFolderPasscode?: string;
    };

type ItemType =
  | "profile-picture"
  | "firm-profile-picture"
  | "matter-file"
  | "firm-file"
  | "message-media"
  | "matter-signature-request-file"
  | "firm-signature-request-file"
  | "matter-template"
  | "matter-share-folder-file";

// All response types have the same structure
export type S3Response = {
  key: string;
  presignedUrl: string;
};

// Response type mapping - all types return the same structure
type ResponseMapping = {
  "profile-picture": S3Response;
  "firm-profile-picture": S3Response;
  "matter-file": S3Response;
  "firm-file": S3Response;
  "message-media": S3Response;
  "matter-template": S3Response;
  "matter-signature-request-file": S3Response;
  "firm-signature-request-file": S3Response;
  "matter-share-folder-file": S3Response;
};

function generateS3ItemPreSignedUrl(itemType: ItemType) {
  return (data: S3RequestData) => {
    switch (itemType) {
      case "profile-picture":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiFirmRoute}/users/profile-picture/pre-signed-url`, data)
          .then((res) => res.data);
      case "firm-profile-picture":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiFirmRoute}/profile-picture/pre-signed-url`, data)
          .then((res) => res.data);
      case "matter-file":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiMatterRoute}/files/pre-signed-url`, data)
          .then((res) => res.data);
      case "firm-file":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiFirmRoute}/files/pre-signed-url`, data)
          .then((res) => res.data);
      case "message-media":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<APIResponse<S3Response>>(
            `${baseContactRoute}/communications/sms/pre-signed-url`,
            {
              clientMethod: data.clientMethod,
              key: data.clientMethod === "get_object" ? data.key : undefined,
              fileName:
                data.clientMethod === "put_object" ? data.fileName : undefined,
            },
          )
          .then((res) => res.data);
      case "matter-signature-request-file":
        if (!data.key || !data.signatureRequestPasscode) {
          throw new Error(
            "A signature request passcode and file key are required to retrieve a signature document.",
          );
        }
        if (data.clientMethod === "delete_object") {
          throw new Error("Deleting signature request files is not supported.");
        }
        return getMatterSignatureRequestPublicFileUrlV2({
          clientMethod: data.clientMethod,
          fileName:
            data.clientMethod === "put_object" ? data.fileName : undefined,
          key: data.key,
          signatureRequestPasscode: data.signatureRequestPasscode,
        });
      case "firm-signature-request-file":
        if (!data.key || !data.signatureRequestPasscode) {
          throw new Error(
            "A signature request passcode and file key are required to retrieve a signature document.",
          );
        }
        if (data.clientMethod === "delete_object") {
          throw new Error("Deleting signature request files is not supported.");
        }
        return getFirmSignatureRequestPublicFileUrlV2({
          clientMethod: data.clientMethod,
          fileName:
            data.clientMethod === "put_object" ? data.fileName : undefined,
          key: data.key,
          signatureRequestPasscode: data.signatureRequestPasscode,
        });
      case "matter-template":
        return authenticatedFastApiAxiosInstanceAllowEmptyString
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiMatterRoute}/templates/pre-signed-url`, data)
          .then((res) => res.data);
      case "matter-share-folder-file":
        return publicFastApiAxiosInstance
          .post<
            APIResponse<S3Response>
          >(`${baseFastApiMatterRoute}/share-folders/files/pre-signed-url`, data)
          .then((res) => res.data);
      default:
        throw new Error("Invalid item type");
    }
  };
}

export function generateS3ItemPreSignedUrlV1<T extends ItemType>(
  itemType: T,
): (data: S3RequestData) => Promise<APIResponse<ResponseMapping[T]>>;
export function generateS3ItemPreSignedUrlV1(itemType: ItemType) {
  return generateS3ItemPreSignedUrl(itemType);
}

export function generatePublicS3ItemPreSignedUrlV1<T extends ItemType>(
  itemType: T,
): (data: S3RequestData) => Promise<APIResponse<ResponseMapping[T]>>;
export function generatePublicS3ItemPreSignedUrlV1(itemType: ItemType) {
  return generateS3ItemPreSignedUrl(itemType);
}
